import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import {
  listPaymentMasters,
  createPaymentMaster,
  updatePaymentMaster,
  deletePaymentMaster,
} from "../../api/paymentsService";
import { paginatedRows } from "../../utils/pagination";
import { formatDate, formatINRExact } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

// Customer-facing notification toggles are hidden until the customer-login
// messaging ships. Flip to true to re-enable the UI; backend already supports it.
const SHOW_CUSTOMER_NOTIFICATIONS = false;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const PATTERN_LABEL = {
  one_off: "One-off",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
};

const GST_LABEL = {
  gst: "GST",
  no_gst: "No GST",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "one_off", label: "One-off" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Quarterly" },
  { id: "half_yearly", label: "Half-yearly" },
  { id: "yearly", label: "Yearly" },
];

export default function PaymentMastersPage() {
  const [tab, setTab] = useState("all");
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', master }

  const masters = useApi(() => listPaymentMasters({ per_page: 100 }));
  const rows = paginatedRows(masters.data);
  const filtered = tab === "all" ? rows : rows.filter((m) => m.recurrence_pattern === tab);

  const onDelete = async (m) => {
    const attached = m.businesses_count ?? 0;
    const note = attached > 0
      ? `\n\nIt's attached to ${attached} business${attached === 1 ? "" : "es"}. The backend will refuse — detach first.`
      : "";
    if (!window.confirm(`Delete payment template "${m.name}"?${note}`)) return;
    try {
      await deletePaymentMaster(m.id);
      showToast("success", "Payment template deleted");
      masters.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Payment Templates</h1>
          <div className="page-sub">Recurring & one-off billing templates that attach to businesses</div>
        </div>
        <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
          <Icon name="plus" size={14} /> New template
        </button>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {FILTERS.map((f) => {
          const count = f.id === "all" ? rows.length : rows.filter((m) => m.recurrence_pattern === f.id).length;
          return (
            <button
              key={f.id}
              className={`tab ${tab === f.id ? "active" : ""}`}
              onClick={() => setTab(f.id)}
            >
              {f.label}
              <span className="tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      {masters.loading ? (
        <LoadingBlock rows={3} height={64} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="dollar-sign"
          title={tab === "all" ? "No payment templates yet" : `No ${PATTERN_LABEL[tab]?.toLowerCase()} templates`}
          hint="Create a template, then attach it to one or more businesses from the business detail page."
        />
      ) : (
        <div className="card">
          <div className="card-body no-pad">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Recurrence</th>
                  <th className="t-num">Default amount</th>
                  <th className="t-num" title="Businesses this template is attached to">Attached</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="clickable" onClick={() => setDrawer({ mode: "edit", master: m })}>
                    <td>
                      <div className="t-primary fw-600">{m.name}</div>
                      {m.description && (
                        <div className="t-muted text-xs" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 360 }}>
                          {m.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="row gap-sm" style={{ alignItems: "center" }}>
                        <span className="badge badge-neutral">{PATTERN_LABEL[m.recurrence_pattern]}</span>
                        <span className={`badge ${m.gst_treatment === "gst" ? "badge-info" : "badge-neutral"}`}>
                          {GST_LABEL[m.gst_treatment] || "No GST"}
                        </span>
                      </div>
                      <div className="t-muted text-xs" style={{ marginTop: 2 }}>{recurrenceSummary(m)}</div>
                    </td>
                    <td className="t-num">{formatINRExact(m.default_amount)}</td>
                    <td className="t-num">{m.businesses_count ?? 0}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row gap-sm">
                        <button className="icon-btn" title="Edit" onClick={() => setDrawer({ mode: "edit", master: m })}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{ color: "var(--danger)" }}
                          onClick={() => onDelete(m)}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PaymentMasterDrawer
        open={!!drawer}
        mode={drawer?.mode}
        master={drawer?.master}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          masters.reload();
        }}
      />
    </>
  );
}

function recurrenceSummary(m) {
  switch (m.recurrence_pattern) {
    case "one_off":
      return m.one_off_due_date ? `Due ${formatDate(m.one_off_due_date)}` : "—";
    case "monthly":
      return `Due day ${m.monthly_due_day ?? "?"} · reminder day ${m.monthly_reminder_day ?? m.monthly_due_day ?? "?"}`;
    case "quarterly": {
      const fmt = (mo, day) => mo && day ? `${MONTHS[mo - 1]} ${day}` : "?";
      // Four short tokens keep the row scannable; full details live in the
      // edit drawer.
      return [1, 2, 3, 4]
        .map((q) => `Q${q} ${fmt(m[`quarterly_${q}_due_month`], m[`quarterly_${q}_due_day`])}`)
        .join(" · ");
    }
    case "half_yearly": {
      const fmt = (mo, day) => mo && day ? `${MONTHS[mo - 1]} ${day}` : "?";
      return `H1 ${fmt(m.half_yearly_1_due_month, m.half_yearly_1_due_day)} · H2 ${fmt(m.half_yearly_2_due_month, m.half_yearly_2_due_day)}`;
    }
    case "yearly":
      return m.yearly_due_month && m.yearly_due_day
        ? `Due ${MONTHS[m.yearly_due_month - 1]} ${m.yearly_due_day}`
        : "—";
    default:
      return "";
  }
}

function emptyForm() {
  return {
    name: "",
    description: "",
    default_amount: "",
    gst_treatment: "no_gst",
    recurrence_pattern: "monthly",
    one_off_due_date: "",
    monthly_due_day: 1,
    monthly_reminder_day: 1,
    // Indian fiscal-year quarters by default — admins can override per cycle.
    quarterly_1_due_month: 6, quarterly_1_due_day: 30,
    quarterly_1_reminder_month: 6, quarterly_1_reminder_day: 20,
    quarterly_2_due_month: 9, quarterly_2_due_day: 30,
    quarterly_2_reminder_month: 9, quarterly_2_reminder_day: 20,
    quarterly_3_due_month: 12, quarterly_3_due_day: 31,
    quarterly_3_reminder_month: 12, quarterly_3_reminder_day: 20,
    quarterly_4_due_month: 3, quarterly_4_due_day: 31,
    quarterly_4_reminder_month: 3, quarterly_4_reminder_day: 20,
    half_yearly_1_due_month: 4,
    half_yearly_1_due_day: 30,
    half_yearly_1_reminder_month: 4,
    half_yearly_1_reminder_day: 20,
    half_yearly_2_due_month: 10,
    half_yearly_2_due_day: 31,
    half_yearly_2_reminder_month: 10,
    half_yearly_2_reminder_day: 20,
    yearly_due_month: 3,
    yearly_due_day: 31,
    yearly_reminder_month: 3,
    yearly_reminder_day: 20,
    reminder_days_before: 3,
    // Customer pings are opt-in — admin enables them after deciding the
    // messaging is right. Matches the column defaults in the migration.
    notify_customer_on_generate: false,
    notify_customer_on_reminder: false,
    notify_customer_on_overdue: false,
    notify_customer_on_receipt: false,
  };
}

function PaymentMasterDrawer({ open, mode = "create", master, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && master) {
      setForm({
        ...emptyForm(),
        ...master,
        // Inputs prefer strings for controlled state.
        description: master.description ?? "",
        default_amount: master.default_amount ?? "",
        one_off_due_date: master.one_off_due_date
          ? String(master.one_off_due_date).slice(0, 10)
          : "",
      });
    } else {
      setForm(emptyForm());
    }
    setErrors({});
  }, [open, mode, master]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (mode === "edit") {
        await updatePaymentMaster(master.id, payload);
        showToast("success", "Template updated");
      } else {
        await createPaymentMaster(payload);
        showToast("success", "Template created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const p = form.recurrence_pattern;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${master?.name || "template"}` : "New payment template"}
      subtitle={mode === "edit" ? "Changes only affect future generated instances; open bills stay at their snapshotted amount." : null}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.name || !form.default_amount}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <Field label="Template name" error={errors.name?.[0]}>
          <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Monthly retainer · GST filing fee" />
        </Field>

        <Field label="Description" hint="Optional — shown to admin only">
          <textarea
            className="textarea"
            rows={2}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            placeholder="Optional internal note"
          />
        </Field>

        <Field label="Default amount (₹)" hint="Per-business overrides happen at the attach step." error={errors.default_amount?.[0]}>
          <div className="row gap-sm" style={{ alignItems: "stretch" }}>
            <input
              className="input"
              type="number"
              step="0.01"
              min="0"
              value={form.default_amount}
              onChange={(e) => set({ default_amount: e.target.value })}
              placeholder="5000.00"
              style={{ flex: 1 }}
            />
            {/* GST add-on helper — only meaningful when the template carries
                GST. Clicking it grosses up the entered base by 18% (rounded
                to 2dp) so admins can set the GST-inclusive figure without a
                calculator. Repeatedly clicking compounds — expected, since
                the resulting figure is what gets written. */}
            {form.gst_treatment === "gst" && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!form.default_amount || Number(form.default_amount) <= 0}
                onClick={() => {
                  const base = Number(form.default_amount || 0);
                  if (!base) return;
                  set({ default_amount: (Math.round(base * 118) / 100).toFixed(2) });
                }}
                title="Add 18% GST to the current amount"
              >
                + 18% GST
              </button>
            )}
          </div>
        </Field>

        <Field label="GST treatment">
          <div className="row gap-sm">
            {Object.entries(GST_LABEL).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`btn btn-sm ${form.gst_treatment === k ? "btn-primary" : "btn-secondary"}`}
                onClick={() => set({ gst_treatment: k })}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Recurrence">
          <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
            {Object.entries(PATTERN_LABEL).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`btn btn-sm ${p === k ? "btn-primary" : "btn-secondary"}`}
                onClick={() => set({ recurrence_pattern: k })}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        {p === "one_off" && (
          <Field label="Due date" error={errors.one_off_due_date?.[0]}>
            <input
              type="date"
              className="input"
              value={form.one_off_due_date}
              onChange={(e) => set({ one_off_due_date: e.target.value })}
            />
          </Field>
        )}

        {p === "monthly" && (
          <div className="row gap-md" style={{ flexWrap: "wrap" }}>
            <Field label="Due day of month" hint="1–31" style={{ flex: 1, minWidth: 160 }}>
              <input type="number" min="1" max="31" className="input"
                value={form.monthly_due_day}
                onChange={(e) => set({ monthly_due_day: Number(e.target.value) || "" })}
              />
            </Field>
            <Field label="Reminder day" hint="Generator fires on this day" style={{ flex: 1, minWidth: 160 }}>
              <input type="number" min="1" max="31" className="input"
                value={form.monthly_reminder_day}
                onChange={(e) => set({ monthly_reminder_day: Number(e.target.value) || "" })}
              />
            </Field>
          </div>
        )}

        {p === "quarterly" && (
          <>
            {[1, 2, 3, 4].map((q) => (
              <CycleRow
                key={q}
                title={`Q${q} (e.g. ${["June", "September", "December", "March"][q - 1]})`}
                dueMonth={form[`quarterly_${q}_due_month`]}
                dueDay={form[`quarterly_${q}_due_day`]}
                remMonth={form[`quarterly_${q}_reminder_month`]}
                remDay={form[`quarterly_${q}_reminder_day`]}
                onChange={(patch) => set({
                  [`quarterly_${q}_due_month`]: patch.dueMonth,
                  [`quarterly_${q}_due_day`]: patch.dueDay,
                  [`quarterly_${q}_reminder_month`]: patch.remMonth,
                  [`quarterly_${q}_reminder_day`]: patch.remDay,
                })}
              />
            ))}
          </>
        )}

        {p === "half_yearly" && (
          <>
            <CycleRow
              title="First cycle (e.g. April)"
              dueMonth={form.half_yearly_1_due_month}
              dueDay={form.half_yearly_1_due_day}
              remMonth={form.half_yearly_1_reminder_month}
              remDay={form.half_yearly_1_reminder_day}
              onChange={(patch) => set({
                half_yearly_1_due_month: patch.dueMonth,
                half_yearly_1_due_day: patch.dueDay,
                half_yearly_1_reminder_month: patch.remMonth,
                half_yearly_1_reminder_day: patch.remDay,
              })}
            />
            <CycleRow
              title="Second cycle (e.g. October)"
              dueMonth={form.half_yearly_2_due_month}
              dueDay={form.half_yearly_2_due_day}
              remMonth={form.half_yearly_2_reminder_month}
              remDay={form.half_yearly_2_reminder_day}
              onChange={(patch) => set({
                half_yearly_2_due_month: patch.dueMonth,
                half_yearly_2_due_day: patch.dueDay,
                half_yearly_2_reminder_month: patch.remMonth,
                half_yearly_2_reminder_day: patch.remDay,
              })}
            />
          </>
        )}

        {p === "yearly" && (
          <CycleRow
            title="Yearly cycle"
            dueMonth={form.yearly_due_month}
            dueDay={form.yearly_due_day}
            remMonth={form.yearly_reminder_month}
            remDay={form.yearly_reminder_day}
            onChange={(patch) => set({
              yearly_due_month: patch.dueMonth,
              yearly_due_day: patch.dueDay,
              yearly_reminder_month: patch.remMonth,
              yearly_reminder_day: patch.remDay,
            })}
          />
        )}

        {SHOW_CUSTOMER_NOTIFICATIONS && (
          <details>
            <summary className="t-primary fw-600" style={{ cursor: "pointer", padding: "8px 0" }}>
              Customer notifications
            </summary>
            <div className="col gap-sm" style={{ paddingTop: 8 }}>
              <Field label="Reminder lead time (days before due date)" hint="0 = no pre-due reminder">
                <input
                  type="number"
                  min="0"
                  max="90"
                  className="input"
                  style={{ maxWidth: 120 }}
                  value={form.reminder_days_before}
                  onChange={(e) => set({ reminder_days_before: Number(e.target.value) || 0 })}
                />
              </Field>
              <Toggle label="Notify customer when a bill is generated" value={form.notify_customer_on_generate} onChange={(v) => set({ notify_customer_on_generate: v })} />
              <Toggle label="Notify customer with the pre-due reminder" value={form.notify_customer_on_reminder} onChange={(v) => set({ notify_customer_on_reminder: v })} />
              <Toggle label="Notify customer when a bill goes overdue" value={form.notify_customer_on_overdue} onChange={(v) => set({ notify_customer_on_overdue: v })} />
              <Toggle label="Notify customer when a payment is received" value={form.notify_customer_on_receipt} onChange={(v) => set({ notify_customer_on_receipt: v })} />
              <div className="text-xs t-muted">
                Customer notifications only fire if the business has a linked customer login.
                Admins are notified regardless.
              </div>
            </div>
          </details>
        )}
      </div>
    </Drawer>
  );
}

function CycleRow({ title, dueMonth, dueDay, remMonth, remDay, onChange }) {
  return (
    <div className="card" style={{ background: "var(--surface-2)" }}>
      <div className="card-body col gap-sm">
        <div className="t-primary fw-600 text-sm">{title}</div>
        <div className="row gap-md" style={{ flexWrap: "wrap" }}>
          <Field label="Due month" style={{ flex: 1, minWidth: 130 }}>
            <select className="select" value={dueMonth || ""} onChange={(e) => onChange({ dueMonth: Number(e.target.value) || null, dueDay, remMonth, remDay })}>
              <option value="">—</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Field label="Due day" style={{ flex: 1, minWidth: 100 }}>
            <DayInput month={dueMonth} value={dueDay}
              onChange={(v) => onChange({ dueMonth, dueDay: v, remMonth, remDay })}
            />
          </Field>
          <Field label="Reminder month" style={{ flex: 1, minWidth: 130 }}>
            <select className="select" value={remMonth || ""} onChange={(e) => onChange({ dueMonth, dueDay, remMonth: Number(e.target.value) || null, remDay })}>
              <option value="">—</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Field label="Reminder day" style={{ flex: 1, minWidth: 100 }}>
            <DayInput month={remMonth} value={remDay}
              onChange={(v) => onChange({ dueMonth, dueDay, remMonth, remDay: v })}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

// Conservative day count per month — February fixed at 28 so a due/reminder
// day never silently vanishes in non-leap years (matches the task-master
// form + the backend generators' clamping).
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function daysInMonth(month) {
  if (!month || month < 1 || month > 12) return 31;
  return DAYS_IN_MONTH[month - 1];
}

// Day picker bounded by the chosen month. If the month changes to a shorter
// one while a too-large day is set (e.g. 31 -> Feb), the day clamps down.
function DayInput({ value, onChange, month }) {
  const max = daysInMonth(month);

  useEffect(() => {
    if (value && value > max) onChange(max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max]);

  return (
    <input
      type="number"
      className="input"
      min={1}
      max={max}
      value={value || ""}
      onChange={(e) => {
        if (!e.target.value) { onChange(null); return; }
        const n = Number(e.target.value);
        if (Number.isNaN(n)) return;
        onChange(Math.min(Math.max(n, 1), max));
      }}
    />
  );
}

function Field({ label, hint, error, children, style }) {
  return (
    <div className="field" style={style}>
      <label className="field-label">{label}</label>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="row gap-sm" style={{ alignItems: "center", cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

/** Strip empty strings & convert numeric strings before POSTing. */
function buildPayload(form) {
  const payload = { ...form };

  // Inputs return "" for cleared numerics; backend wants null.
  for (const k of Object.keys(payload)) {
    if (payload[k] === "") payload[k] = null;
  }
  payload.default_amount = Number(payload.default_amount);
  payload.reminder_days_before = Number(payload.reminder_days_before) || 0;

  return payload;
}
